import { Box, Button, Typography } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { isViewOnly } from "../utils/permissions";
import { Layer } from "../types/layer";
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Filter } from "../components/layerControl/FilterModal";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { DrawingFeatureType, DrawingMode } from "../contexts/DrawingContext";
import { useDate } from "../contexts/DateContext";
import { StrategyContext } from "../contexts/StrategyContext";
import { IgnitionRegimeContext, IgnitionRegimeProvider } from "../contexts/IgnitionRegimeContext";
import { MapProvider } from "../contexts/MapContext";
import StrategiesBar from "../components/StrategiesBar";
import FooterStatusBar from "../components/FooterStatusBar";
import FuelLoadStatusSnackbar from "../components/FuelLoadStatusSnackbar";
import Map from "../components/Map";
import LayersToolbar from "../components/toolbar/LayersToolbar";
import SimulationToolbar from "../components/toolbar/SimulationToolbar";
import LayerControlPanel from "../components/layerControl/LayerControlPanel";
import ProjectBoundingBoxLayer from "../components/layer_components/ProjectBoundingBoxLayer";
import AssetsLayer from "../components/layer_components/AssetsLayer";
import FireHistoryLayer from "../components/layer_components/FireHistoryLayer";
import CustomLayersLayer from "../components/layer_components/CustomLayersLayer";
import VegetationLayer from "../components/layer_components/VegetationLayer";
import NotesLayer from "../components/layer_components/NotesLayer";
import FuelLoadLayer from "../components/layer_components/FuelLoadLayer";
import FuelLoad3DLayer from "../components/layer_components/FuelLoad3DLayer";
import GrassCuringLayer from "../components/layer_components/GrassCuringLayer";
import TreatmentsLayer from "../components/layer_components/TreatmentsLayer";
import IgnitionsLayer from "../components/layer_components/IgnitionsLayer";
import DrawingLayer from "../components/layer_components/DrawingLayer";
import MeasureLayer from "../components/layer_components/MeasureLayer";
import RiskFromLayer from "../components/layer_components/RiskFromLayer";
import RiskToLayer from "../components/layer_components/RiskToLayer";
import AnimationLayer from "../components/layer_components/AnimationLayer";
import UrbanBuildingStateLayer from "../components/layer_components/UrbanBuildingStateLayer";
import UrbanCrownStateLayer from "../components/layer_components/UrbanCrownStateLayer";
import UrbanIgnitionEdgesLayer from "../components/layer_components/UrbanIgnitionEdgesLayer";
import UrbanLegend from "../components/layer_components/UrbanLegend";
import { UrbanAnimationProvider, useUrbanAnimation } from "../contexts/UrbanAnimationContext";
import { EnsembleMemberProvider } from "../contexts/EnsembleMemberContext";
import AddEditAssetModal from "../components/AddEditAssetModal";
import AddEditTreatmentModal from "../components/AddEditTreatmentModal";
import AddNoteModal from "../components/AddNoteModal";
import AddIgnitionModal from "../components/AddIgnitionModal";
import AddFireHistoryModal, { AddFireHistoryModalRef } from "../components/AddFireHistoryModal";
import EditVegetationModal from "../components/EditVegetationModal";
import InspectionLayer from "../components/layer_components/InspectionLayer";
import PropertyBoundaryLayer from "../components/layer_components/PropertyBoundaryLayer";
import RealtimeObservationsLayer from "../components/layer_components/RealtimeObservationsLayer";
import IncidentsLayer, {
  DEFAULT_INCIDENT_GROUPS,
  IncidentGroupVisibility,
} from "../components/layer_components/IncidentsLayer";
import IncidentDetailModal from "../components/incidents/IncidentDetailModal";
import { DEFAULT_INCIDENT_DAYS, IncidentSummary } from "../api/incidentLog";
import { RealtimeBbox } from "../api/realtimeObservations";
import { bboxFromBoundary } from "../utils/boundaryUtils";
import ForestEditIcon from "../components/icons/ForestEditIcon";
import BoltPlusIcon from "../components/icons/BoltPlusIcon";

import { useProject } from "../api/project";
import { useExpandProject, ExpandPayload } from "../api/project";
import { useAuth } from "../contexts/AuthContext";
import { useMap } from "../contexts/MapContext";
import ProjectExpandButtons from "../components/layer_components/ProjectExpandButtons";
import LayerStatusBar from "../components/LayerStatusBar";
import {useTimezone} from "../contexts/TimezoneContext.tsx";
import ProjectConfigModal from "../components/ProjectConfigModal";
import { useProjectConfig } from "../contexts/ProjectConfigContext";
import { DataReviewProvider, useDataReview } from "../contexts/DataReviewContext";
import { invalidateAfterDataApply, useDataReviewLandings } from "../api/dataReview";
import DataReviewLayer from "../components/layer_components/DataReviewLayer";
import DataReviewImageryLayers from "../components/layer_components/DataReviewImageryLayers";
import DataReviewImageryPreviewLayer from "../components/layer_components/DataReviewImageryPreviewLayer";
import DataReviewAreaMaskLayer from "../components/layer_components/DataReviewAreaMaskLayer";

import FactCheckIcon from "@mui/icons-material/FactCheck";
import VegEditorClearedLayer from "../components/layer_components/VegEditorClearedLayer";
import VegEditor from "../components/vegEditor/VegEditor";
import VegEditorBar from "../components/vegEditor/VegEditorBar";
import { VegEditorProvider, useVegEditor } from "../components/vegEditor/VegEditorContext";
import { useEditorData } from "../components/vegEditor/useEditorData";
import { clearedWashVisible, termOf } from "../components/vegEditor/editorModel";
dayjs.extend(utc);
dayjs.extend(timezone);

const ProjectContent = () => {
  const { selectedStrategy, refreshStrategy } = useContext(StrategyContext);
  const { selectedRegime } = useContext(IgnitionRegimeContext);
  void selectedRegime;
  const { project } = useProject();
  const { setProjectTimezone } = useTimezone();
  const { setSelectedDate } = useDate();
  const { isConfigModalOpen, closeConfigModal } = useProjectConfig();
  const { map } = useMap();
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const canExpandProject =
    !viewOnly &&
    (user?.is_staff || user?.capabilities?.some(c => c.codename === 'infinite_project'));
  const { projectId, incidentId: routeIncidentIdParam } = useParams();
  const navigate = useNavigate();
  // VEGETATION EDITOR — the same map with the refinement's panels floating
  // over it: the run's photo, the cleared ground of a clean slate, and its
  // findings and paint, one in hand at a time.
  // View-only users cannot change the vegetation, so the editor's address
  // takes them to the project map instead.
  const vegRoute = !!useMatch("/projects/:projectId/vegetation");
  const vegMode = vegRoute && !viewOnly;
  useEffect(() => {
    if (vegRoute && viewOnly) navigate(`/projects/${projectId}`, { replace: true });
  }, [vegRoute, viewOnly, projectId]);
  const { currentId, setCurrentId, selectedIds, setSelectedIds, showImagery, showChanges, pickedImagery, tracedArea } = useDataReview();
  const editor = useVegEditor();
  const editorData = useEditorData(vegMode);
  // The refinement open in the editor, with the photo its run read.
  const detailJob = vegMode && editor.phase === "session" ? editorData.job : null;
  const detailOverlays = detailJob?.overlays ?? [];

  // INCIDENTS — selection shared by the map layer, the popover, the detail
  // modal and the Incidents tab. A deep link (/projects/:id/incidents/:iid,
  // as in notification emails) lands on the map with that incident
  // selected, zoomed to, and its detail modal open.
  const routeIncidentId = routeIncidentIdParam ? Number(routeIncidentIdParam) : null;
  const [selectedIncidentId, setSelectedIncidentId] = useState<number | null>(null);
  const [detailIncidentId, setDetailIncidentId] = useState<number | null>(null);
  const [incidentFocusSignal, setIncidentFocusSignal] = useState(0);
  const [incidentDays, setIncidentDays] = useState(DEFAULT_INCIDENT_DAYS);
  const [incidentGroups, setIncidentGroups] =
    useState<IncidentGroupVisibility>(DEFAULT_INCIDENT_GROUPS);
  useEffect(() => {
    if (routeIncidentId === null || Number.isNaN(routeIncidentId)) return;
    setSelectedIncidentId(routeIncidentId);
    setDetailIncidentId(routeIncidentId);
    setIncidentFocusSignal((s) => s + 1);
  }, [routeIncidentId]);
  const focusIncident = (id: number) => {
    setSelectedIncidentId(id);
    setIncidentFocusSignal((s) => s + 1);
  };
  const closeIncidentDetail = () => {
    setDetailIncidentId(null);
    if (routeIncidentId !== null) navigate(`/projects/${projectId}`, { replace: true });
  };

  const pendingKey = projectId ? `pendingTiles_${projectId}` : null;
  const [pendingTiles, setPendingTiles] = useState<Record<string, ExpandPayload>>(() => {
    if (!projectId) return {};
    try { return JSON.parse(localStorage.getItem(`pendingTiles_${projectId}`) ?? "null") ?? {}; } catch { return {}; }
  });
  const queryClient = useQueryClient();
  // An apply or an undo of a vegetation refinement landing, wherever the
  // user is by then (the editor, or the project map after Save & exit):
  // the layers it feeds and the strategy are refreshed.
  useDataReviewLandings(projectId, () => {
    invalidateAfterDataApply(queryClient, projectId);
    refreshStrategy();
  });
  const { mutate: expandProject } = useExpandProject(projectId);
  const pendingTilesRef = useRef(pendingTiles);
  const fireHistoryModalRef = useRef<AddFireHistoryModalRef | null>(null);

  useEffect(() => {
    pendingTilesRef.current = pendingTiles;
  }, [pendingTiles]);

  const handleExpand = (payload: ExpandPayload) => {
    const key = `${payload.direction}-${payload.tile_col}-${payload.tile_row}`;
    setPendingTiles((prev) => ({ ...prev, [key]: payload }));
    expandProject(payload);
  };

  useEffect(() => {
    if (!pendingKey) return;
    if (Object.keys(pendingTiles).length > 0) {
      localStorage.setItem(pendingKey, JSON.stringify(pendingTiles));
    } else {
      localStorage.removeItem(pendingKey);
    }
  }, [pendingTiles, pendingKey]);

  useEffect(() => {
    if (project?.data?.status === 'ready') {
      if (Object.keys(pendingTilesRef.current).length > 0) {
        refreshStrategy();
        queryClient.invalidateQueries({ queryKey: ['vegetationSources'] });
        queryClient.setQueryData(['vegetation-tile-version', projectId], () => Date.now());
      }
      setPendingTiles({});
    }
  }, [project?.data?.status]);

  useEffect(() => {
      // Only update if the project data and timezone exist
      if (project?.data?.timezone) {
          setProjectTimezone(project.data.timezone);
          setSelectedDate(dayjs().tz(project.data.timezone));
      }

      // Optional: Cleanup when leaving the project view
      return () => setProjectTimezone(null);
  }, [project?.data?.timezone, setProjectTimezone]);

  // Filter state for each layer
  const [treatmentsFilters, setTreatmentsFilters] = useState<Filter[]>([]);

  useEffect(() => {
    setTreatmentsFilters([]);
  }, [selectedStrategy?.id]);
  const [ignitionsFilters, setIgnitionsFilters] = useState<Filter[]>([]);
  const [notesFilters, setNotesFilters] = useState<Filter[]>([]);
  const [fireHistoryFilters, setFireHistoryFilters] = useState<Filter[]>([]);
  const [propertiesFilters, setPropertiesFilters] = useState<Filter[]>([]);
  const [customLayersFilters, setCustomLayersFilters] = useState<Filter[]>([]);
  const [hotspotsFilters, setHotspotsFilters] = useState<Filter[]>([]);
  const [lightningFilters, setLightningFilters] = useState<Filter[]>([]);
  const [hazardsFilters, setHazardsFilters] = useState<Filter[]>([]);
  const [ignitionPointsFilters, setIgnitionPointsFilters] = useState<Filter[]>([]);
  const [burntAreasFilters, setBurntAreasFilters] = useState<Filter[]>([]);

  // PROJECT LAYERS
  const boundingBoxLayer = ProjectBoundingBoxLayer(
    "bounding-box",
    "Project Area",
    project?.data.boundary
  );
  const propertiesLayer = PropertyBoundaryLayer(
    "properties",
    "Properties",
    true
  );
  const assetsLayer = AssetsLayer("assets", "Assets");
  const notesLayer = NotesLayer("notes", "Notes", notesFilters);
  const fireHistoryLayer = FireHistoryLayer("fire-history", "Fire History", fireHistoryFilters);
  const customLayersLayer = CustomLayersLayer("custom-layers", "Custom Layers", customLayersFilters);
  const projectLayers = [
    boundingBoxLayer,
    propertiesLayer,
    assetsLayer,
    notesLayer,
    fireHistoryLayer,
    customLayersLayer,
  ];

  // REALTIME DETECTION LAYERS — fetch over the project bbox buffered by
  // ~0.5° (≈50 km), matching the backend ingestion coverage buffer.
  const projectBoundary = project?.data?.boundary ?? null;
  const getRealtimeBbox = useCallback((): RealtimeBbox | null => {
    const b = bboxFromBoundary(projectBoundary);
    if (!b) return null;
    return {
      top: b.top + 0.5,
      bottom: b.bottom - 0.5,
      left: b.left - 0.5,
      right: b.right + 0.5,
    };
  }, [projectBoundary]);
  // Realtime kinds are an organisation-level entitlement; a project shows
  // whatever its tenant has available, with no per-project opt-in. A kind
  // counts as available only once the server has said so — while the project
  // is still loading nothing realtime is offered, rather than flashing up
  // layers the organisation may not be entitled to.
  const realtimeAvailable = project?.data?.realtime_available;
  const kindAvailable = (kind: string) => realtimeAvailable?.[kind] === true;
  // No kind at all: the whole Realtime row goes, incidents included — every
  // incident is raised from a detection, so there can never be one.
  const realtimeEntitled = Object.values(realtimeAvailable ?? {}).some(Boolean);
  const hotspotsLayer = RealtimeObservationsLayer({
    id: "realtime-hotspots",
    name: "Hotspots",
    kind: "hotspot",
    getBbox: getRealtimeBbox,
    available: kindAvailable("hotspot"),
    filters: hotspotsFilters,
    scopeNote: "within ~50 km of this project",
    // Enables cluster hulls + the create-ignition actions (project map only).
    projectIdForIgnitions: projectId ? Number(projectId) : undefined,
    projectBoundary: projectBoundary,
  });
  const lightningLayer = RealtimeObservationsLayer({
    id: "realtime-lightning",
    name: "Lightning",
    kind: "lightning",
    getBbox: getRealtimeBbox,
    available: kindAvailable("lightning"),
    filters: lightningFilters,
    scopeNote: "within ~50 km of this project",
    // Strikes seed point ignitions only — no cluster polygons (see the layer).
    projectIdForIgnitions: projectId ? Number(projectId) : undefined,
    projectBoundary: projectBoundary,
  });
  const hazardsLayer = RealtimeObservationsLayer({
    id: "realtime-hazards",
    name: "Hazards incidents",
    kind: "hazard",
    getBbox: getRealtimeBbox,
    available: kindAvailable("hazard"),
    filters: hazardsFilters,
    scopeNote: "within ~50 km of this project",
    // An agency incident is a real fire someone may want to simulate, so
    // point ignitions apply; warning areas are information only.
    projectIdForIgnitions: projectId ? Number(projectId) : undefined,
    projectBoundary: projectBoundary,
  });
  // The organisation's own ArcGIS feeds: reported ignition points and
  // mapped burnt areas. Both can seed ignitions (a point, or the burnt
  // footprint as a polygon), so the project-map actions apply.
  const ignitionPointsLayer = RealtimeObservationsLayer({
    id: "realtime-ignition-points",
    name: "Ignition points",
    kind: "ignition",
    getBbox: getRealtimeBbox,
    available: kindAvailable("ignition"),
    filters: ignitionPointsFilters,
    scopeNote: "within ~50 km of this project",
    projectIdForIgnitions: projectId ? Number(projectId) : undefined,
    projectBoundary: projectBoundary,
  });
  const burntAreasLayer = RealtimeObservationsLayer({
    id: "realtime-burnt-areas",
    name: "Burnt areas",
    kind: "burnt_area",
    getBbox: getRealtimeBbox,
    available: kindAvailable("burnt_area"),
    filters: burntAreasFilters,
    scopeNote: "within ~50 km of this project",
    projectIdForIgnitions: projectId ? Number(projectId) : undefined,
    projectBoundary: projectBoundary,
  });
  const realtimeLayers = [
    hotspotsLayer,
    lightningLayer,
    hazardsLayer,
    ignitionPointsLayer,
    burntAreasLayer,
  ];

  // The project's own incidents (open, silent rekindles, closed history),
  // on by default and independent of which detection layers above are on —
  // but not of the entitlement behind them all.
  const incidentsLayer = IncidentsLayer({
    id: "incidents",
    name: "Incidents",
    projectId: projectId ? Number(projectId) : undefined,
    days: incidentDays,
    groups: incidentGroups,
    selectedIncidentId,
    onSelect: setSelectedIncidentId,
    focusSignal: incidentFocusSignal,
    onOpenDetail: setDetailIncidentId,
    available: realtimeEntitled,
  });

  // STRATEGY LAYERS
  const vegetationLayer = VegetationLayer("vegetation", "Vegetation");
  const fuelLoadLayer = FuelLoadLayer("fuel-load", "Fuel Load");
  const fuelLoad3DLayer = FuelLoad3DLayer("fuel-load-3d", "3D Layers");
  // Landscape context, not a simulation result: available whether or not a
  // simulation has been run.
  const grassCuringLayer = GrassCuringLayer(
    "grass-curing", "Grass Curing", projectId ? Number(projectId) : undefined,
  );
  const strategyLayers = [
    vegetationLayer,
    // Refined areas' detail, drawn over the vegetation from zoom 13.
    vegetationLayer.fineLayer,
    fuelLoadLayer,
    fuelLoad3DLayer,
    grassCuringLayer,
  ];

  // TREATMENT & IGNITION LAYERS
  const treatmentsLayer = TreatmentsLayer("treatments", "Treatments", treatmentsFilters);
  const ignitionsLayer = IgnitionsLayer("ignitions", "Ignitions", ignitionsFilters);
  const treatmentIgnitionLayers = [
    treatmentsLayer,
    ignitionsLayer,
  ];

  // FIRE SIMULATION LAYERS
  const riskFromLayer = RiskFromLayer("risk-from", "Risk From");
  const riskToLayer = RiskToLayer("risk-to", "Risk To");
  const animationLayer = AnimationLayer({
    id: "animation-layer",
    name: "Animation",
    frameType: "fireline_intensity",
  });
  const inspectionLayer = InspectionLayer("inspection-layer", "Inspect");
  const simulationLayers = [
    riskFromLayer,
    riskToLayer,
    inspectionLayer,
    animationLayer,
  ];

  // DRAWING LAYERS
  const drawAssetLayer = DrawingLayer(
    "create-asset",
    "Create Asset",
    "add_business",
    DrawingMode.ASSET,
    [
      DrawingFeatureType.POLYGON,
      DrawingFeatureType.LINE,
      DrawingFeatureType.POINT,
      DrawingFeatureType.UPLOAD,
    ]
  );
  const drawTreatmentLayer = DrawingLayer(
    "create-treatment",
    "Create Treatment",
    "loupe",
    DrawingMode.TREATMENT,
    [DrawingFeatureType.POLYGON, DrawingFeatureType.LINE, DrawingFeatureType.UPLOAD],
    () => {
      return !!selectedStrategy;
    }
  );
  const drawNoteLayer = DrawingLayer(
    "create-note",
    "Create Note",
    "edit_note",
    DrawingMode.NOTE,
    [DrawingFeatureType.POINT]
  );
  const drawIgnitionLayer = DrawingLayer(
    "create-ignition",
    "Create Ignition",
    <BoltPlusIcon />,
    DrawingMode.IGNITION,
    [
      DrawingFeatureType.POINT,
      DrawingFeatureType.LINE,
      DrawingFeatureType.POLYGON,
    ]
  );
  const drawVegetationEditLayer = DrawingLayer(
    "edit-vegetation",
    "Edit Vegetation",
    <ForestEditIcon />,
    DrawingMode.VEGETATION_EDIT,
    [DrawingFeatureType.POLYGON, DrawingFeatureType.LINE],
    () => {
      return !!selectedStrategy;
    }
  );
  const drawDataAreaLayer = DrawingLayer(
    "data-area",
    "Area to look at",
    <FactCheckIcon />,
    DrawingMode.DATA_AREA,
    [DrawingFeatureType.POLYGON]
  );
  // The shapes in view (the server leaves out rejected ones and those under
  // the cut-off), plus the shape in hand wherever it is, so a mistaken
  // reject can still be seen and taken back.
  const reviewFeatures = useMemo(() => {
    if (!vegMode || editor.phase !== "session" || editor.before) return [];
    const cur = editorData.current;
    return cur && !editorData.features.some((f) => f.properties.id === cur.properties.id) ? [...editorData.features, cur] : editorData.features;
  }, [vegMode, editor.phase, editor.before, editorData.features, editorData.current]);
  const dataReviewLayer = DataReviewLayer(
    "data-review",
    reviewFeatures,
    currentId,
    (id) => {
      // In the editor a click picks a shape only while selecting; the brush,
      // the wand and a lasso in progress own the map then.
      if (vegMode && (editor.mode !== "review" || editor.tool !== "select")) return;
      setSelectedIds([]); setCurrentId(id);
      if (vegMode) { const f = editorData.features.find((x) => x.properties.id === id); if (f) editor.setActiveTerm(termOf(f)); }
    },
    showChanges,
    vegMode ? selectedIds : [],
    editorData.colours,
  );
  const dataReviewImageryLayers = DataReviewImageryLayers("data-review-imagery", detailOverlays, showImagery);
  // The area stays outlined through every step: the traced polygon until
  // the parse starts, then the run's own area for parsing, looking and review.
  const detailJobArea = detailJob?.area
    ? ({ type: "Feature", properties: {}, geometry: detailJob.area } as GeoJSON.Feature)
    : null;
  const outlinedArea = !vegMode ? null : editor.phase === "setup" ? tracedArea : editor.phase === "session" ? detailJobArea : null;
  const dataReviewPreviewLayer = DataReviewImageryPreviewLayer(
    "data-review-imagery-preview", pickedImagery, vegMode && editor.phase === "setup" && showImagery, outlinedArea,
  );
  const dataReviewMaskLayer = DataReviewAreaMaskLayer("data-review-area-mask", outlinedArea, vegMode && editor.phase !== "home");
  // A clean slate: the ground inside the area reads as cleared until it is
  // mapped again. Previewed while choosing it, shown through the refinement,
  // and lifted by the review sheet's Before.
  const clearedVisible = clearedWashVisible({
    clean: vegMode && !editor.before && (
      (editor.phase === "setup" && editor.setup.startMode === "clean" && editor.mode !== "paint") ||
      (editor.phase === "session" && editorData.isClean)
    ),
    vegLayerOn: !!vegetationLayer.active,
    photoOver: showImagery && (editor.phase === "setup" ? !!pickedImagery : detailOverlays.length > 0),
  });
  const vegEditorClearedLayer = VegEditorClearedLayer("veg-editor-cleared", outlinedArea, clearedVisible);
  const measureLayer = MeasureLayer("measure", "Measure");
  // View-only users get no create/edit drawing tools — measure only.
  const drawingLayers = viewOnly
    ? [measureLayer]
    : [
        drawAssetLayer,
        drawTreatmentLayer,
        drawIgnitionLayer,
        drawNoteLayer,
        drawVegetationEditLayer,
        drawDataAreaLayer,
        measureLayer,
      ];
  const toolbarLayers: Layer[] = [];

  // A failed initialization leaves the project with no usable layers — show a
  // terminal screen instead of the map so it can't be "loaded" via direct URL
  // (#184). Placed after all hooks so hook order stays stable across renders.
  if (project?.data?.status === "failed") {
    return (
      <Box
        sx={{
          height: "calc(var(--vh100) - var(--topbar-h))",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 56, color: "#ef4444" }} />
        <Typography variant="h6">Project initialization failed</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 440, textAlign: "center" }}
        >
          This project failed during initialization and can't be opened. You
          can archive it from the projects page and create it again.
        </Typography>
        <Button variant="outlined" onClick={() => navigate("/projects")}>
          Back to projects
        </Button>
      </Box>
    );
  }

  return (
      <Box
        sx={{
          height: "calc(var(--vh100) - var(--topbar-h))",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* The editor puts its own bar where the strategies bar goes. */}
        {!vegMode && <StrategiesBar />}
        {vegMode && <VegEditorBar />}
        <FooterStatusBar />
        <FuelLoadStatusSnackbar />
        <Box sx={{ display: "flex", width: "100%", alignContent: "center" }}>
          <AddEditAssetModal mode="add" />
          <AddEditTreatmentModal mode="add" />
          <AddNoteModal />
          <AddIgnitionModal />
          <AddFireHistoryModal ref={fireHistoryModalRef} />
          <EditVegetationModal />
          <UrbanBuildingStateLayer />
          <UrbanCrownStateLayer />
          <UrbanIgnitionEdgesLayer />
          <Map
            layers={[
              ...projectLayers,
              ...realtimeLayers,
              incidentsLayer,
              ...strategyLayers,
              ...treatmentIgnitionLayers,
              ...drawingLayers,
              ...simulationLayers,
              // Always present so leaving the screen clears them (the map only
              // updates layers it is handed; it never removes missing ones).
              // Photo first so the proposed changes draw over it: the picked
              // source as tiles, then the chunks a run actually read.
              dataReviewPreviewLayer,
              dataReviewMaskLayer,
              ...dataReviewImageryLayers,
              vegEditorClearedLayer,
              dataReviewLayer,
            ]}
            searchable={false}
          />
          {vegMode && <VegEditor vegetationLayer={vegetationLayer} />}
          <LayerStatusBar />
          {canExpandProject && !vegMode && (
            <ProjectExpandButtons
              map={map}
              boundary={project?.data?.boundary}
              project={project?.data}
              pendingTiles={pendingTiles}
              onExpand={handleExpand}
            />
          )}

          {!vegMode && <Box
            sx={{
              position: "fixed",
              alignContent: "center",
              bottom: 50,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 50,
              flexDirection: "row",
              zIndex: 100,
            }}
          >
            <SimulationToolbar
              layers={simulationLayers}
              direction={"row"}
              labelSide={"top"}
            />
          </Box>}
          {!vegMode && <Box
            sx={{
              position: "fixed",
              alignContent: "center",
              top: 0,
              bottom: 0,
              left: 10,
              zIndex: 100,
            }}
          >
            <LayersToolbar layers={toolbarLayers} labelSide={"right"} />
          </Box>}
          {!vegMode && <LayerControlPanel
            vegetationLayer={vegetationLayer}
            fuelLoadLayer={fuelLoadLayer}
            fuelLoad3DLayer={fuelLoad3DLayer}
            grassCuringLayer={grassCuringLayer}
            assetsLayer={assetsLayer}
            fireHistoryModalRef={fireHistoryModalRef}
            treatmentsLayer={treatmentsLayer}
            treatmentsFilters={treatmentsFilters}
            onTreatmentsFiltersChange={setTreatmentsFilters}
            ignitionsLayer={ignitionsLayer}
            ignitionsFilters={ignitionsFilters}
            onIgnitionsFiltersChange={setIgnitionsFilters}
            notesLayer={notesLayer}
            notesFilters={notesFilters}
            onNotesFiltersChange={setNotesFilters}
            fireHistoryLayer={fireHistoryLayer}
            fireHistoryFilters={fireHistoryFilters}
            onFireHistoryFiltersChange={setFireHistoryFilters}
            propertyBoundaryLayer={propertiesLayer}
            propertyBoundaryFilters={propertiesFilters}
            onPropertyBoundaryFiltersChange={setPropertiesFilters}
            hotspotsLayer={hotspotsLayer}
            hotspotsFilters={hotspotsFilters}
            onHotspotsFiltersChange={setHotspotsFilters}
            hotspotsAvailable={kindAvailable("hotspot")}
            lightningLayer={lightningLayer}
            lightningFilters={lightningFilters}
            onLightningFiltersChange={setLightningFilters}
            lightningAvailable={kindAvailable("lightning")}
            hazardsLayer={hazardsLayer}
            hazardsFilters={hazardsFilters}
            onHazardsFiltersChange={setHazardsFilters}
            hazardsAvailable={kindAvailable("hazard")}
            ignitionPointsLayer={ignitionPointsLayer}
            ignitionPointsFilters={ignitionPointsFilters}
            onIgnitionPointsFiltersChange={setIgnitionPointsFilters}
            ignitionPointsAvailable={kindAvailable("ignition")}
            burntAreasLayer={burntAreasLayer}
            burntAreasFilters={burntAreasFilters}
            onBurntAreasFiltersChange={setBurntAreasFilters}
            burntAreasAvailable={kindAvailable("burnt_area")}
            incidentsLayer={realtimeEntitled ? incidentsLayer : null}
            incidentDays={incidentDays}
            onIncidentDaysChange={setIncidentDays}
            incidentGroups={incidentGroups}
            onIncidentGroupsChange={setIncidentGroups}
            customLayersLayer={customLayersLayer}
            customLayersFilters={customLayersFilters}
            onCustomLayersFiltersChange={setCustomLayersFilters}
            customLayersSelectedIds={customLayersLayer.selectedIds}
            onCustomLayersToggle={customLayersLayer.toggleLayer}
            measureLayer={measureLayer}
          />}
        </Box>
        <ProjectConfigModal
          open={isConfigModalOpen}
          onClose={closeConfigModal}
          onSelectIncident={(incident: IncidentSummary) => {
            // A card on the Incidents tab: show it on the map behind.
            closeConfigModal();
            focusIncident(incident.id);
          }}
        />
        {projectId && (
          <IncidentDetailModal
            projectId={projectId}
            incidentId={detailIncidentId}
            onClose={closeIncidentDetail}
            onSelectIncident={(id) => {
              // Lineage links inside the modal switch it to that incident
              // and move the map behind it.
              setDetailIncidentId(id);
              focusIncident(id);
            }}
          />
        )}
      </Box>
  );
};

const UrbanLegendWrapper: React.FC = () => {
  const { selectedIgnitionInfo } = useUrbanAnimation();
  const hasBurntUrbanAreas = !!selectedIgnitionInfo && (
    Object.keys(selectedIgnitionInfo.urban_asset_burnout_s ?? {}).length > 0
    || Object.keys(selectedIgnitionInfo.urban_crown_ignition_s ?? {}).length > 0
  );
  const hasBurntCrowns = !!selectedIgnitionInfo && Object.keys(selectedIgnitionInfo.urban_crown_ignition_s ?? {}).length > 0;
  return <UrbanLegend hasBurntUrbanAreas={hasBurntUrbanAreas} hasBurntCrowns={hasBurntCrowns} />;
};

export default function ProjectPage() {
  return (
    <MapProvider>
      <IgnitionRegimeProvider>
        <UrbanAnimationProvider>
          <EnsembleMemberProvider>
            <DataReviewProvider>
              <VegEditorProvider>
                <ProjectContent />
              </VegEditorProvider>
            </DataReviewProvider>
            <UrbanLegendWrapper />
          </EnsembleMemberProvider>
        </UrbanAnimationProvider>
      </IgnitionRegimeProvider>
    </MapProvider>
  );
}
